import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Flame, ImageDown } from "lucide-react";
import axios from "axios";
import { renderStreakCard, shareStreakCard } from "../lib/streakCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Public streak badge — the shareable proof of a 4/12/26/52-week run. */
export default function StreakBadgePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const saveCard = async () => {
    setBusy(true);
    try {
      const blob = await renderStreakCard({
        weeks: data.milestone,
        chairName: data.chair_name,
        onTimeRate: data.on_time_rate,
        mintedLabel: new Date(data.minted_at).toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      await shareStreakCard(blob, data.milestone);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    axios.get(`${BACKEND}/api/streak/badge/public/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail?.message || "This badge link is no longer available."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#8a8a8a] flex items-center justify-center p-8">
        <p data-testid="badge-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0a0a0a] text-[#8a8a8a] flex items-center justify-center p-8">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa] flex items-center justify-center p-6">
      <div
        className="relative w-full max-w-md rounded-lg border border-[#c89f45]/30 bg-[#101010] p-7 sm:p-10 text-center overflow-hidden"
        data-testid="badge-card"
      >
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ background: "radial-gradient(circle at 50% 0%, #c89f45 0%, transparent 60%)" }}
        />
        <div className="relative">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#c89f45]/60 mb-6">
            <Flame size={26} className="text-[#c89f45]" />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#c89f45]">Boardroom · streak badge</p>
          <h1 className="font-serif text-5xl sm:text-6xl mt-4" data-testid="badge-milestone">{data.milestone} weeks</h1>
          <p className="text-[#bbb] mt-3 leading-relaxed">
            {data.chair_name} has chaired their own board {data.milestone} weeks running.
          </p>
          <div className="flex items-center justify-center gap-6 sm:gap-8 mt-8 pt-6 border-t border-[#2a2a2a] flex-wrap">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a]">Streak</p>
              <p className="font-serif text-2xl mt-1">{data.streak_weeks}w</p>
            </div>
            {data.on_time_rate != null && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a]">On time</p>
                <p className="font-serif text-2xl mt-1">{data.on_time_rate}%</p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a]">Minted</p>
              <p className="font-serif text-2xl mt-1">{new Date(data.minted_at).toLocaleDateString(undefined, { month: "short", year: "2-digit" })}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
            <button
              type="button"
              onClick={saveCard}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md bg-[#c89f45] hover:bg-[#b48c34] disabled:opacity-60 text-[#0a0a0a] text-sm font-medium transition-colors"
              data-testid="badge-save-card"
            >
              <ImageDown size={14} /> {busy ? "Making the card…" : "Get the share card"}
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center h-11 px-4 rounded-md border border-[#2a2a2a] text-sm text-[#c89f45] hover:border-[#c89f45] transition-colors"
              data-testid="badge-cta"
            >
              Start your own board →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
